export type ProfileSummary = {
  token: string;
  ip: string;
  method: string;
  url: string;
  time: string;
  statusCode: number | null;
  parentToken: string | null;
  traceId: string | null;
  route: string | null;
  user: string | null;
};

export type CollectorSummary = {
  name: string;
  badge: string | null;
  available: boolean;
};

export type ProfileRedirect = {
  token: string | null;
  method: string;
  route: string;
  statusCode: number;
};

export type Profile = {
  summary: ProfileSummary;
  collectors: CollectorSummary[];
  children: ProfileSummary[];
  redirect: ProfileRedirect | null;
};

export type Collector = {
  name: string;
  class: string;
  data: Record<string, unknown>;
};

export type SpanEvent = {
  name: string;
  time: number;
  attributes: Record<string, string | number | boolean | null>;
};

export type Span = {
  spanId: string;
  parentSpanId: string | null;
  name: string;
  kind:
    "internal" | "server" | "client" | "producer" | "consumer" | "unspecified";
  service: string;
  scope: string;
  start: number;
  duration: number;
  status: "unset" | "ok" | "error";
  statusMessage: string | null;
  attributes: Record<string, string | number | boolean | null>;
  events: SpanEvent[];
};

export type Trace = {
  traceId: string;
  startedAt: string;
  duration: number;
  services: string[];
  spans: Span[];
};

export type RouteTrace = {
  name: string | null;
  path: string | null;
  level: 0 | 1 | 2;
  log: string;
};

export type TraceSummary = {
  traceId: string;
  rootName: string;
  rootService: string;
  startedAt: string;
  duration: number;
  hasError: boolean;
  route: string | null;
  method: string | null;
  statusCode: number | null;
};

export type TimeRange = "15m" | "1h" | "6h" | "24h" | "7d";

export type TraceFilters = {
  range?: TimeRange;
  route?: string;
  method?: string;
  statusCode?: number;
  minDuration?: number;
  errors?: boolean;
  limit?: number;
};

export type LogEntry = {
  timestamp: string;
  level: string;
  channel: string;
  message: string;
  traceId: string | null;
  runtime: string | null;
  user: string | null;
  exceptionType: string | null;
  attributes: Record<string, string>;
};

export type LogSeries = {
  level: string;
  points: Array<{ time: string; count: number }>;
};

export type LogFacet = { name: string; count: number };

export type LogSearch = {
  start: string;
  end: string;
  total: number;
  errors: number;
  warnings: number;
  failedTraces: number;
  step: number;
  series: LogSeries[];
  levels: LogFacet[];
  runtimes: LogFacet[];
  users: LogFacet[];
  channels: LogFacet[];
  exceptions: LogFacet[];
  entries: LogEntry[];
};

export type LogFilters = {
  range?: TimeRange;
  // Comma separated Loki levels, as the API takes them.
  levels?: string;
  channel?: string;
  runtime?: string;
  search?: string;
  traceId?: string;
  noise?: boolean;
  limit?: number;
  user?: string;
};

export type MetricSeries = {
  name: "rate" | "errors" | "p50" | "p95" | string;
  points: Array<{ time: string; value: number | null }>;
};

export type RouteMetrics = {
  route: string;
  method: string;
  calls: number;
  errors: number;
  p95: number | null;
  average: number | null;
  trend: Array<number | null>;
};

export type MetricsTotals = {
  calls: number;
  errors: number;
  p50: number | null;
  p95: number | null;
};

export type InstrumentationSide = {
  calls: number;
  p50: number | null;
  p95: number | null;
};

export type InstrumentationRoute = {
  route: string;
  method: string;
  instrumented: InstrumentationSide;
  plain: InstrumentationSide;
};

// Cost of the debug-only collectors on the production kernel, the same
// window split by the ecopro.profiler.instrumented resource attribute.
export type Instrumentation = {
  instrumented: InstrumentationSide;
  plain: InstrumentationSide;
  routes: InstrumentationRoute[];
};

export type Metrics = {
  start: string;
  end: string;
  step: number;
  calls: number;
  errors: number;
  p50: number | null;
  p95: number | null;
  series: MetricSeries[];
  routes: RouteMetrics[];
  instrumentation: Instrumentation | null;
  previous: MetricsTotals | null;
};

export type MetricsMethod = "GET" | "POST" | "other";

export type MetricsFilters = {
  range?: TimeRange;
  route?: string;
  method?: MetricsMethod;
};

export type RealtimeNode = {
  uid: string;
  name: string;
  version: string;
  clients: number;
  users: number;
  subscriptions: number;
  channels: number;
  uptime: number;
  cpu: number | null;
  memory: number | null;
};

export type RealtimeChannel = {
  name: string;
  namespace: string;
  clients: number;
};

export type Realtime = {
  clients: number;
  users: number;
  subscriptions: number;
  nodes: RealtimeNode[];
  channels: RealtimeChannel[];
};

export type RealtimeClient = {
  client: string;
  user: string;
  connection: string | null;
  subscription: string | null;
};

export type RealtimePublication = {
  offset: number;
  data: string;
};

export type RealtimeChannelDetail = {
  name: string;
  hasPresence: boolean;
  clients: number;
  users: number;
  presence: RealtimeClient[];
  hasHistory: boolean;
  history: RealtimePublication[];
};

export type RealtimeFilters = {
  channel?: string;
};

export type DoctrineQuery = {
  index: number;
  sql: string;
  params: string[];
  duration: number;
  share: number;
  occurrences: number;
  explainable: boolean;
};

export type DoctrineConnection = {
  name: string;
  count: number;
  time: number;
  queries: DoctrineQuery[];
  grouped: DoctrineQuery[];
};

export type DoctrineEntity = { class: string; errors: string[] };

export type DoctrineManager = { name: string; entities: DoctrineEntity[] };

export type Doctrine = {
  queryCount: number;
  groupedQueryCount: number;
  time: number;
  invalidEntityCount: number;
  cache: { enabled: boolean; hits: number; misses: number; puts: number };
  connections: DoctrineConnection[];
  managers: DoctrineManager[];
};

export type Collection = {
  enabled: boolean;
  always: boolean;
  slowThreshold: number;
};

export type ProfileFilters = {
  ip?: string;
  url?: string;
  method?: string;
  statusCode?: string;
  route?: string;
  user?: string;
  traceId?: string;
  start?: number;
  end?: number;
  limit?: number;
};

export type DataNode =
  | { t: "scalar"; type: string; v: string | number | boolean | null }
  | { t: "string"; v: string; bin: boolean; cut: number }
  | {
      t: "array";
      kind: "indexed" | "assoc";
      count: number;
      cut: number;
      id?: number;
      children: DataChild[];
    }
  | {
      t: "object";
      class: string;
      cut: number;
      id?: number;
      children: DataChild[];
    }
  | { t: "resource"; class: string; cut: number; children: DataChild[] }
  | { t: "ref"; to: number };

export type DataChild = {
  key: string | number | null;
  vis?: "public" | "protected" | "private" | "virtual";
  class?: string;
  node: DataNode;
};

export function isDataNode(value: unknown): value is DataNode {
  return (
    "object" === typeof value &&
    null !== value &&
    "t" in value &&
    "string" === typeof (value as { t: unknown }).t
  );
}

export type Worker = {
  host: string;
  pid: number;
  /** Null until a worker started by hand handled its first message */
  transport: string | null;
  /** Started by app:worker, which restarts it */
  supervised: boolean;
  state: "idle" | "busy";
  startedAt: string;
  handled: number;
  memory: number;
  message: string | null;
  updatedAt: string;
};

export type WorkerPool = {
  transport: string;
  desired: number;
  running: number;
  restarts: number;
  lastExit: string | null;
};

export type WorkerSupervisor = {
  host: string;
  pid: number;
  startedAt: string;
  pools: WorkerPool[];
  updatedAt: string;
};

export type WorkerQueue = {
  transport: string;
  stream: string;
  length: number;
  waiting: number;
  pending: number;
};

export type Workers = {
  supervisors: WorkerSupervisor[];
  workers: Worker[];
  queues: WorkerQueue[];
};

export type BackupRun = {
  kind: string;
  trigger: string;
  startedAt: string;
  finishedAt: string | null;
  success: boolean | null;
  exitCode: number | null;
  error: string | null;
  tail: string[];
};

/**
 * Currently supported backup sources, add new ones here, for example:
 *
 *     export type BackupSource = "database" | "odoo";
 */
export type BackupSource = "database";

export type Backup = {
  configured: boolean;
  running: boolean;
  kinds: string[];
  lastDumpSuccess: string | null;
  runs: BackupRun[];
};

export type BackupResult =
  { backup: Backup; error: null } | { backup: null; error: string };
